<script lang="ts">
import { h as createElement } from 'vue'
import FieldGroupPropsMixin from './FieldGroupPropsMixin.vue'
import HintText from 'objects/hint/HintText.vue'

export default {
  props: {
    isCheckboxGroup: { type: Boolean, default: false },
    isRadioGroup: { type: Boolean, default: false }
  },
  mixins: [FieldGroupPropsMixin], // contains majority of the props
  components: { 'tt-hint-text': HintText },
  computed: {
    hint() {
      return {
        text: this.errorText || this.hintText,
        type: this.errorText ? 'error' : 'info'
      }
    },
    asFieldset() {
      return this.isCheckboxGroup || this.isRadioGroup
    }
  },
  methods: {
    getHintTextElement(aboveField = false) {
      return createElement(HintText, {
        id: this.describedbyId,
        class: { 'tt-o-hint--above': aboveField },
        // props
        type: this.hint.type,
        text: this.hint.text
      })
    },
    getLabelElement() {
      const labelChildren = []
      labelChildren.push(createElement('span', {
        class: ['tt-o-field-group__label-text'],
        // adding a space to the label in case the required indicator follows
        innerHTML: this.label
      }))
      if (this.required) {
        labelChildren.push(createElement('span', {
          class: ['tt-o-field-group__required'],
          innerHTML: ` ${this.$msg('fieldGroup.requiredIndicator')}`
        }))
      }
      return createElement(this.asFieldset ? 'legend' : 'label', {
        class: 'tt-o-field-group__label',
        // attrs
        id: this.labelId ?? this.rgLabelId,
        for: this.id
      }, labelChildren)
    }
  },
  /* This is what the html template would look like.  Converted to a render function so this
   * component could also be used for fields like checkbox groups that should use a root element
   * of "fieldset" with "legend" as a label instead of the default root element of a "div" with
   * "label".
   *
   * <div class="tt-o-field-group tt-u-spacing--xs">
   *   <label class="tt-o-field-group__label" :for="id">
   *     <span class="tt-o-field-group__label-text>{{ label }}</span>
   *     <span v-if="required" class="tt-o-field-group__required"
   *           v-html="$msg('fieldGroup.requiredIndicator')"></span>
   *   </label>
   *   <slot></slot>
   *   <tt-hint-text v-if="hint.text" :type="hint.type" :text="hint.text"></tt-hint-text>
   * </div>
   */
  render() {
    const rootChildren = []
    if (this.label) {
      rootChildren.push(this.getLabelElement())
    }
    if (this.hintTextAboveField && this.hint.text) {
      rootChildren.push(this.getHintTextElement(true))
    }
    rootChildren.push(this.$slots.default())
    if (!this.hintTextAboveField && this.hint.text) {
      rootChildren.push(this.getHintTextElement())
    }
    return createElement(
      this.asFieldset ? 'fieldset' : 'div',
      {
        class: 'tt-o-field-group tt-u-spacing--xs',
        // attrs
        'aria-required': this.asFieldset && this.required ? 'true' : null,
        'aria-invalid': this.asFieldset && this.errorText ? 'true' : null,
        'aria-labelledby': this.isRadioGroup ? (this.labelId ?? this.rgLabelId) : null,
        role: this.isRadioGroup ? 'radiogroup' : null
      },
      rootChildren
    )
  },
  created() {
    // radiogroup must have a labelId.  If not passed, need to generate one
    // doesn't need to be reactive since just setting once. Therefore, making sure it is defined
    // by setting to null at least.
    this.rgLabelId = null
    if (this.isRadioGroup && !this.labelId) {
      this.rgLabelId = `tt-rg-${Math.random().toString(16).slice(5)}`
    }
  }
}
</script>

<style lang="scss" src="./field-group.scss"></style>
